import breakpoints from 'base/constants/breakpoints';
import styled from 'styled-components';
import { colors, Card, Typography } from 'syngenta-digital-cropwise-react-ui-kit';

export const Container = styled.div`
  width: 100%;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and ${breakpoints.maxWidth.sm} {
    gap: 16px;
    padding: 16px 16px 18px;
    border-bottom: 2px solid rgba(0, 0, 0, 0);
    background: ${colors.neutral00};
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled(Typography.Title)`
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.0125em;
  color: ${colors.neutral100};
  margin: 0 !important;
  padding: 0;

  @media screen and ${breakpoints.maxWidth.sm} {
    font-family: Noto Sans;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
  }
`;

export const Subtitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #232630;
  letter-spacing: -0.0075em;
  margin: 0;

  @media screen and ${breakpoints.maxWidth.sm} {
    font-size: 14px;
    line-height: 20px;
    color: #808080;
  }
`;

export const SectionContent = styled.div`
  display: flex;
  width: 100%;
  margin: 0;
  flex-direction: column;
  gap: 24px;

  @media screen and ${breakpoints.maxWidth.sm} {
    gap: 16px;
  }
`;

export const CardStyled = styled(Card)`
  border: none !important;
  background: transparent !important;
  .syt-antd-card-body {
    padding: 0;
  }
`;

export const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 256px);
  gap: 16px;
  align-items: end;

  @media screen and ${breakpoints.maxWidth.md} {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and ${breakpoints.maxWidth.sm} {
    grid-template-columns: 1fr;
  }
`;

export const ControlField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  @media screen and ${breakpoints.maxWidth.sm} {
    gap: 2px;
  }

  .field-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 400;
    color: ${colors.neutral100};
    letter-spacing: -0.0075em;
  }

  label.field-label {
    font-size: 14px;
    font-weight: 400;
    color: ${colors.neutral100};
  }

  .field-hint {
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    color: ${colors.neutral60};
  }

  .syt-antd-select-dropdown,
  .syt-antd-picker,
  .syt-antd-select,
  .ant-picker,
  .ant-select {
    width: 100%;
    font-size: 14px;
  }

  .syt-antd-picker,
  .ant-picker,
  .syt-antd-select .syt-antd-select-selector,
  .ant-select .ant-select-selector {
    height: 40px !important;
    border-radius: 4px !important;
    border: 1px solid ${colors.neutral30} !important;
    background-color: ${colors.neutral10};

    @media screen and ${breakpoints.maxWidth.sm} {
      background-color: ${colors.neutral00};
    }
  }

  .syt-antd-select .syt-antd-select-selector,
  .ant-select .ant-select-selector {
    display: flex;
    align-items: center;
    padding: 0 12px !important;
  }

  .syt-antd-select-selection-item,
  .ant-select-selection-item,
  .syt-antd-select-selection-placeholder,
  .ant-select-selection-placeholder {
    line-height: 34px !important;
  }

  .syt-antd-select-arrow,
  .ant-select-arrow {
    color: ${colors.neutral70};
    transition: transform 0.3s;
  }

  .syt-antd-select-open .syt-antd-select-arrow,
  .ant-select-open .ant-select-arrow {
    transform: rotate(180deg);
  }
`;

export const NoWrapFieldLabel = styled.span.attrs({
  className: 'field-label',
})`
  white-space: nowrap;
`;

export const ConditionDropdown = styled.div`
  padding: 8px;

  .dropdown-header {
    font-size: 13px;
    color: ${colors.neutral60};
    padding: 4px 8px 8px;
    text-align: center;
  }

  .dropdown-option {
    padding: 8px 16px;
    text-align: center;
    border: 1px solid ${colors.neutral30};
    border-radius: 4px;
    margin-bottom: 6px;
    cursor: pointer;
    font-size: 14px;
    color: ${colors.neutral100};
    transition: background-color 0.2s;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background-color: ${colors.neutral10};
    }

    &.selected {
      background-color: ${colors.blue10};
      border-color: ${colors.blue40};
      color: ${colors.blue60};
      font-weight: 500;
    }
  }
`;

export const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid ${colors.blue50};
  color: ${colors.blue50};
  font-size: 10px;
  cursor: pointer;
  font-style: italic;
  font-weight: 600;
`;

export const ErrorBanner = styled.div`
  background-color: ${colors.red10};
  border: 1px solid ${colors.red30};
  color: ${colors.red60};
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

export const TableWrapper = styled.div`
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid ${colors.neutral10};
  position: relative;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: fixed;

    thead {
      background-color: ${colors.neutral20};
    }

    th,
    td {
      width: 50%;
      padding: 0px 16px;
      text-align: left;
      color: ${colors.neutral100};
      height: 52px;
      box-sizing: border-box;
      letter-spacing: -0.0075em;
    }

    th {
      font-weight: 600;
      color: ${colors.neutral100};
      white-space: nowrap;
      border-bottom: 1px solid ${colors.neutral20};
      background-color: ${colors.neutral20};

      .th-content {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .sort-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 4px;
        background-color: ${colors.neutral20};
        border: none;
        padding: 3px;
        cursor: pointer;
        color: ${colors.neutral100};

        &:hover {
          background-color: ${colors.neutral30};
        }
      }
    }

    tbody tr {
      background-color: ${colors.neutral10};
      border-bottom: 1px solid ${colors.neutral20};
    }

    tbody tr:last-child {
      border-bottom: none;
    }

    .product-name {
      font-weight: 600;
      color: ${colors.neutral100};
    }

    .empty-cell {
      color: ${colors.neutral60};
    }

    .unsupported-cell {
      color: ${colors.red60};
      font-size: 13px;
      font-style: italic;
    }
  }
`;

export const EmptyAdjustedOverlay = styled.div`
  position: absolute;
  right: 0;
  top: 53px;
  bottom: 0;
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  pointer-events: none;
  background-color: ${colors.neutral10};
  color: ${colors.neutral100};
  font-size: 14px;
  text-align: center;
  letter-spacing: -0.0075em;

  svg {
    width: 49px;
    height: 44px;
  }

  @media screen and ${breakpoints.maxWidth.md} {
    position: static;
    width: 100%;
    padding: 24px;
  }
`;

export const MobileCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const MobileCard = styled.div`
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  padding: 16px;
  background-color: ${colors.neutral00};

  .product-name {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.neutral100};
    margin-bottom: 8px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 14px;
    color: ${colors.neutral80};

    .value {
      color: ${colors.neutral100};
      font-weight: 500;
    }
  }
`;

export const MobileTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  position: relative;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
    letter-spacing: -0.0075em;
    table-layout: fixed;
  }

  thead th {
    background-color: ${colors.neutral10};
    font-weight: 600;
    color: ${colors.neutral100};
    height: 28px;
    padding: 0 8px;
    text-align: left;
    white-space: nowrap;
    border-top: 1px solid ${colors.neutral20};
    font-size: 10px;
    line-height: 16px;

    &:first-child {
      border-left: 1px solid ${colors.neutral20};
    }

    &:last-child {
      border-right: 1px solid ${colors.neutral20};
    }
  }

  tbody td {
    background-color: ${colors.neutral00};
    color: ${colors.neutral100};
    height: 28px;
    padding: 0 8px;
    text-align: left;
    font-size: 10px;
    line-height: 16px;
    border: 1px solid ${colors.neutral20};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:first-child {
      border-right-color: ${colors.neutral30};
    }
  }

  .mobile-empty-overlay {
    background-color: ${colors.neutral00};
    border: 1px solid ${colors.neutral20};
    border-left: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 2px;

    svg {
      width: 32px;
      height: 29px;
    }

    .mobile-empty-text {
      font-size: 10px;
      line-height: 16px;
      text-align: center;
      color: ${colors.neutral60};
    }
  }
`;
